<script setup lang="ts">
import { ref, watch, onMounted } from 'vue';
import { submitFeedback } from '../services/feedbackApi';

const STORAGE_KEY = 'yoga-feedback';

interface FeedbackData {
  stayRating: number;
  stayFeedback: string;
  improvementFeedback: string;
}

const stayRating = ref(0);
const stayFeedback = ref('');
const improvementFeedback = ref('');
const isSubmitting = ref(false);
const error = ref<string | null>(null);
const submitSuccess = ref(false);

// Load saved feedback data on component mount
onMounted(() => {
  const savedFeedback = localStorage.getItem(STORAGE_KEY);
  if (savedFeedback) {
    const data: FeedbackData = JSON.parse(savedFeedback);
    stayRating.value = data.stayRating;
    stayFeedback.value = data.stayFeedback;
    improvementFeedback.value = data.improvementFeedback;
  }
});

// Watch for changes and save to localStorage
watch(
  [stayRating, stayFeedback, improvementFeedback],
  () => {
    const feedbackData: FeedbackData = {
      stayRating: stayRating.value,
      stayFeedback: stayFeedback.value,
      improvementFeedback: improvementFeedback.value,
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(feedbackData));
  },
  { deep: true }
);

const setRating = (rating: number) => {
  stayRating.value = rating;
};

async function handleSubmit() {
  try {
    isSubmitting.value = true;
    error.value = null;
    
    await submitFeedback({
      stayRating: stayRating.value,
      stayFeedback: stayFeedback.value,
      improvementFeedback: improvementFeedback.value,
    });
    
    submitSuccess.value = true;
    localStorage.removeItem(STORAGE_KEY);
  } catch (e) {
    error.value = e instanceof Error ? e.message : 'Ein Fehler ist aufgetreten';
  } finally {
    isSubmitting.value = false;
  }
}
</script>

<template>
  <div class="feedback-container">
    <div class="feedback-content">
      <div v-if="submitSuccess" class="success-message">
        Vielen Dank für dein Feedback!
      </div>
      
      <template v-else>
        <div class="feedback-section">
          <h3>Wie hat dir der Aufenthalt gefallen?</h3>
          <textarea
            v-model="stayFeedback"
            rows="4"
            placeholder="Dein Feedback..."
          ></textarea>
        </div>

        <div class="feedback-section">
          <h3>Was können wir deiner Meinung nach besser machen?</h3>
          <textarea
            v-model="improvementFeedback"
            rows="4"
            placeholder="Deine Vorschläge..."
          ></textarea>
        </div>

        <div class="feedback-section">
          <h3>Wie würdest du den Aufenthalt bewerten?</h3>
          <div class="star-rating">
            <button
              v-for="n in 5"
              :key="n"
              @click="setRating(n)"
              :class="{ active: n <= stayRating }"
              class="star-button"
            >
              ★
            </button>
          </div>
          <div class="rating-text">{{ stayRating }} von 5</div>
        </div>

        <div v-if="error" class="error-message">
          {{ error }}
        </div>

        <button 
          class="submit-button" 
          @click="handleSubmit"
          :disabled="isSubmitting || !stayRating"
        >
          {{ isSubmitting ? 'Wird gesendet...' : 'Feedback einreichen' }}
        </button>
      </template>
    </div>
  </div>
</template>

<style scoped>
.feedback-container {
  position: fixed;
  top: 48px;
  left: 0;
  right: 0;
  bottom: 48px;
  overflow-y: auto;
  background: #f5f5f5;
  padding: 1rem;
}

.feedback-content {
  max-width: 600px;
  margin: 0 auto;
}

.feedback-section {
  background: white;
  padding: 1.5rem;
  border-radius: 8px;
  margin-bottom: 1rem;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

h3 {
  color: #960040;
  margin-bottom: 1rem;
  font-size: 1.1rem;
}

textarea {
  width: 100%;
  padding: 0.8rem;
  border: 1px solid #ddd;
  border-radius: 4px;
  resize: vertical;
  font-family: inherit;
  background-color: #f8f8f8;
  color: #333;
}

textarea:focus {
  outline: none;
  border-color: #960040;
  background-color: #fff;
}

.star-rating {
  display: flex;
  gap: 0.5rem;
  justify-content: center;
  margin: 1rem 0;
}

.star-button {
  background: none;
  border: none;
  font-size: 2rem;
  color: #ddd;
  cursor: pointer;
  padding: 0;
}

.star-button.active {
  color: #ffd21f;
}

.rating-text {
  text-align: center;
  color: #666;
}

.submit-button {
  width: 100%;
  padding: 1rem;
  background: #960040;
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 1.1rem;
  font-weight: 500;
  cursor: pointer;
  margin-top: 1rem;
  transition: background-color 0.2s;
}

.submit-button:hover:not(:disabled) {
  background: #7d0035;
}

.submit-button:disabled {
  background: #ccc;
  cursor: not-allowed;
}

.error-message {
  color: #dc2626;
  text-align: center;
  margin: 1rem 0;
  padding: 0.5rem;
  background: #fee2e2;
  border-radius: 4px;
}

.success-message {
  text-align: center;
  padding: 2rem;
  background: white;
  border-radius: 8px;
  color: #166534;
  font-size: 1.2rem;
  font-weight: 500;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}
</style>